import React, { useState, useEffect, useContext } from "react";
import _ from "lodash";
import axios from "axios";
import { useIntl } from "react-intl";
import { Box } from "@mui/material";
import { useTheme } from "@mui/styles";
import {
  Button,
  COLOR,
  VARIANT,
} from "@metacrm/metacrm-material-ui/dist/Button";
import Modal from "@mui/material/Modal";
import { useDispatch } from "react-redux";
import { apiUrl } from "features/configure";
import { useOutletContext } from "react-router";
import { PerformanceContext } from "../context/PerformanceProvider";
import useNavigateEntity from "hooks/useNavigateEntity";
import {
  StyledBasicModalContainer,
  StyledModalContentContainer,
  StyledModalTitle,
  StyledModalContent,
  StyledButtonRowArea,
} from "features/metadesk/chat/BasicModal.styles";
import Checkbox from "@metacrm/metacrm-material-ui/dist/Checkbox";
import AutoCompleteDropdown from "@metacrm/metacrm-material-ui/dist/AutoCompleteDropdown";
import { parseToOptionFormat } from "features/metadesk/settings/Macros/CreateMacroModal/dropdown.utils";
import { useFetchTags } from "features/metadesk/redux/fetchTags";
import useDispatchSnackbar from "hooks/useDispatchSnackbar";
import LoadingIcon from "components/LoadingComponent/LoadingIcon";
import IconTooltip from "@metacrm/metacrm-material-ui/dist/IconTooltip";

const CDPExportPopover = ({ onClose, metaData }) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const { navigateEntity } = useNavigateEntity();
  const dispatchSnackbar = useDispatchSnackbar();
  const [entityName] = useOutletContext();

  const { dateRange } = useContext(PerformanceContext);

  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [walletCount, setWalletCount] = useState("");
  const [source, setSource] = useState("");
  const [status, setStatus] = useState(false);

  const convertISOString = (date) => {
    return date == "all" ? "all" : date.toISOString();
  };
  const [selectedTags, setSelectedTags] = useState([]);

  useEffect(() => {
    if (metaData) {
      const trafficSource = _.get(metaData, "trafficSource", "all");
      const objectiveStatus = _.get(metaData, "objectiveStatus", {
        achieved: true,
        unachieved: true,
      });
      setSource(trafficSource);
      setStatus(objectiveStatus);
      fetchPreviewData();
      setIsSuccess(false);
    }
  }, [metaData]);

  useEffect(() => {
    fetchPreviewData();
  }, [status]);

  const fetchPreviewData = async (defaultData = {}) => {
    if (!status) return;
    try {
      setIsLoading(true);
      setWalletCount("");
      const payload = {
        trafficSource: source,
        objectiveStatus: status,
        startDate: dateRange[0],
        endDate: dateRange[1],
        ...defaultData,
      };

      const { data } = await axios.post(
        apiUrl + `/api/campaignAudience/${entityName}/export/preview`,
        payload
      );

      setWalletCount(_.get(data, "walletCount", 0));
    } catch (err) {
      console.log("e: ", err);
      dispatchSnackbar({ err });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      let payload = {
        trafficSource: source,
        objectiveStatus: status,
        startDate: dateRange[0],
        endDate: dateRange[1],
      };

      if (selectedTags.length) {
        payload.tagIds = _.map(selectedTags, (t) => t._id);
      }

      await axios.post(
        apiUrl + `/api/campaignAudience/${entityName}/export`,
        payload
      );

      setIsSuccess(true);
    } catch (err) {
      dispatchSnackbar({ err });
    } finally {
      setIsExporting(false);
    }
  };

  async function handleChooseTag(newValue) {
    setSelectedTags([...newValue]);
  }

  if (isSuccess) {
    return (
      <Modal open={metaData} className={"modal"} onClose={onClose}>
        <div className="modalPaper" style={{ maxWidth: "520px" }}>
          <StyledBasicModalContainer>
            <div
              style={{
                padding: "24px 24px 0px",
                display: "flex",
                width: "100%",
                justifyContent: "flex-end",
              }}>
              <div
                style={{ fontSize: 24, color: theme.customColors.grey[700] }}
                onClick={onClose}>
                <i className="meta-crm-icon-ic_cancel" />
              </div>
            </div>
            <StyledModalContentContainer
              style={{
                rowGap: 24,
                padding: "24px 68px 56px",
              }}>
              <StyledModalContent textAlign={"center"}>
                <div style={{ color: "#555555" }}>
                  <div style={{ fontSize: 48, marginBottom: 24 }}>{"🎉"}</div>
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: theme.fontWeight.BOLD,
                      color: "#222222",
                    }}>
                    {formatMessage({ id: "global.status.exportComplete" })}
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      paddingTop: 12,
                      color: theme.customColors.grey[600],
                    }}>
                    {formatMessage({
                      id: "websiteTraffic.exportWallets.completionDescription",
                    })}
                  </div>
                  <div style={{ marginTop: 36 }}>
                    <Button
                      variant={VARIANT.OUTLINED}
                      onClick={onClose}
                      style={{ width: 120, padding: 2 }}>
                      {formatMessage({ id: "global.btn.checkLater" })}
                    </Button>
                    <Button
                      color={COLOR.SECONDARY}
                      onClick={() => {
                        let url = `/customers/list?fromSource=${"websiteTraffic"}`;
                        navigateEntity(url);
                      }}
                      style={{ marginLeft: 12, width: 120 }}>
                      {formatMessage({ id: "global.btn.goToCDP" })}
                    </Button>
                  </div>
                </div>
              </StyledModalContent>
            </StyledModalContentContainer>
          </StyledBasicModalContainer>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={metaData} className={"modal"} onClose={onClose}>
      <div className="modalPaper" style={{ maxWidth: "640px" }}>
        <StyledBasicModalContainer>
          <div
            style={{
              padding: 24,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}>
            <StyledModalTitle textAlign={"left"} style={{ margin: 0 }}>
              {formatMessage({
                id: "websiteTraffic.dropdown.exportWalletsToCDP",
              })}
            </StyledModalTitle>
            <div
              style={{ fontSize: 24, color: theme.customColors.grey[700] }}
              onClick={onClose}>
              <i className="meta-crm-icon-ic_cancel" />
            </div>
          </div>
          <Box
            sx={{
              width: "100%",
              height: "1px",
              backgroundColor: "#E5E5E5",
            }}
          />
          <StyledModalContentContainer
            style={{ rowGap: 24, padding: "12px 32px 24px" }}>
            <div
              style={{
                color: theme.customColors.grey[600],
                fontSize: 14,
                lineHeight: "20px",
              }}>
              {formatMessage({
                id: "websiteTraffic.exportWallets.description",
              })}
            </div>
            <StyledModalContent textAlign={"left"}>
              <div style={{ color: "#555555" }}>
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: theme.fontWeight.BOLD,
                  }}>
                  {formatMessage({ id: "global.column.trafficSource" })}
                </div>
                <div style={{ fontSize: 14, paddingTop: 8 }}>
                  {source === "all"
                    ? formatMessage({ id: "global.taskStatus.all" })
                    : source}
                </div>
              </div>
            </StyledModalContent>
            <StyledModalContent textAlign={"left"}>
              <div style={{ color: "#555555" }}>
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: theme.fontWeight.BOLD,
                  }}>
                  {"Objective Status"}
                </div>
                <div
                  style={{
                    fontSize: 14,
                    paddingTop: 8,
                    display: "flex",
                    columnGap: 30,
                  }}>
                  <Checkbox
                    onChange={() => {
                      const currVal = status;
                      setStatus({
                        ...currVal,
                        achieved: !currVal.achieved,
                      });
                    }}
                    label={formatMessage({ id: "global.status.achieved" })}
                    checked={_.get(status, "achieved", false)}
                    controlStyle={{ color: "#222222" }}
                  />
                  <Checkbox
                    onChange={() => {
                      const currVal = status;
                      setStatus({
                        ...currVal,
                        unachieved: !currVal.unachieved,
                      });
                    }}
                    label={formatMessage({ id: "global.status.unachieved" })}
                    checked={_.get(status, "unachieved", false)}
                    controlStyle={{ color: "#222222" }}
                  />
                </div>
              </div>
            </StyledModalContent>
            <StyledModalContent textAlign={"left"} style={{ marginBottom: 0 }}>
              <div style={{ color: "#555555" }}>
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: theme.fontWeight.BOLD,
                  }}>
                  {formatMessage({ id: "global.dropdown.addTags" })}
                  <span style={{ fontWeight: "normal", marginLeft: 4 }}>
                    {formatMessage({ id: "global.annotation.optional" })}
                  </span>
                </div>
                <div style={{ fontSize: 14, paddingTop: 8 }}>
                  <AddTag
                    selectedTags={selectedTags}
                    entityName={entityName}
                    handleChooseTag={handleChooseTag}
                  />
                </div>
              </div>
            </StyledModalContent>
          </StyledModalContentContainer>
          <Box
            sx={{
              width: "100%",
              height: "1px",
              backgroundColor: "#E5E5E5",
            }}
          />
          <StyledButtonRowArea style={{ padding: "16px 24px" }}>
            <Button
              style={{ width: "120px" }}
              variant={VARIANT.OUTLINED}
              disabled={isExporting}
              onClick={onClose}>
              {formatMessage({ id: "global.btn.cancel" })}
            </Button>
            <Button
              color={COLOR.SECONDARY}
              onClick={handleExport}
              disabled={isLoading || isExporting || !walletCount}
              style={{ marginLeft: 12, marginRight: 8 }}
              isLoading={isExporting}
              loadingText={"Exporting..."}>
              {isLoading
                ? "Calculating..."
                : formatMessage(
                    {
                      id: "websiteTraffic.btn.exportWallets",
                    },
                    { n: walletCount }
                  )}
            </Button>
          </StyledButtonRowArea>
        </StyledBasicModalContainer>
      </div>
    </Modal>
  );
};

function AddTag({ entityName, handleChooseTag, selectedTags = [], ...props }) {
  const { formatMessage } = useIntl();
  const { tags, addTag } = useFetchTags();

  const options = parseToOptionFormat(tags[entityName]);

  const handleCreateNewTag = async (value) => {
    if (!value) return;
    if (!_.find(tags[entityName], { value: value.toString() })) {
      await addTag({
        tag: {
          value: value,
        },
        entityName,
      });
    }
  };

  const [dropdownOpen, setDropdownOpen] = useState(false);

  function handleChange(event, value) {
    handleChooseTag(value);
  }

  return (
    <AutoCompleteDropdown
      multiple
      isTag
      placeholder={formatMessage({ id: "global.placeholder.searchOrCreate" })}
      value={selectedTags}
      onChange={handleChange}
      open={dropdownOpen}
      onClick={() => setDropdownOpen(!dropdownOpen)}
      onClose={() => setDropdownOpen(false)}
      options={options}
      onEnter={(value) => {
        handleCreateNewTag(value);
      }}
      {...props}
    />
  );
}

export default CDPExportPopover;
